import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui/Button';
import { Shield, Sparkles, Menu, X, Github } from 'lucide-react';
import { trackNavClick, trackOutboundClick } from '../lib/analytics';

export const Header: React.FC = () => {
  const { t } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToWaitlist = () => {
    setMobileMenuOpen(false);
    trackNavClick('#waitlist-section', 'Header Join Waitlist');
    const element = document.getElementById('waitlist-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-navy-950/95 backdrop-blur-md border-b border-slate-200 dark:border-navy-800/80 py-3 shadow-sm dark:shadow-surface'
          : 'bg-transparent py-4 sm:py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2.5 group focus:outline-none shrink-0">
            <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-navy-850 border border-gold-500/40 flex items-center justify-center shadow-gold-glow group-hover:border-gold-400 transition-all">
              <Shield className="w-5 h-5 text-gold-500 group-hover:scale-105 transition-transform" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-1.5">
                Workouse <span className="text-gold-500 font-extrabold">AI</span>
              </span>
              <span className="text-[10px] uppercase font-mono tracking-widest text-slate-500 dark:text-slate-400 -mt-1">
                Zero-Trust Gateway
              </span>
            </div>
          </a>

          {/* Streamlined Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-medium text-slate-700 dark:text-slate-300">
            <a
              href="#features"
              onClick={() => trackNavClick('#features', t.nav.features)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.features}
            </a>
            <a
              href="#dlp"
              onClick={() => trackNavClick('#dlp', t.nav.dlpVault)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.dlpVault}
            </a>
            <a
              href="#budget"
              onClick={() => trackNavClick('#budget', t.nav.budget)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.budget}
            </a>
            <a
              href="#analytics"
              onClick={() => trackNavClick('#analytics', t.nav.analytics)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.analytics}
            </a>
            <a
              href="#architecture"
              onClick={() => trackNavClick('#architecture', t.nav.architecture)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.architecture}
            </a>
            <a
              href="#faq"
              onClick={() => trackNavClick('#faq', t.nav.faq)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors focus:outline-none"
            >
              {t.nav.faq}
            </a>
          </nav>

          {/* Right Action Cluster */}
          <div className="hidden sm:flex items-center gap-2.5 shrink-0">
            {/* GitHub Icon Link */}
            <a
              href="https://github.com/workouse"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick('github', 'https://github.com/workouse')}
              className="p-2 rounded-lg bg-slate-100 dark:bg-navy-850 hover:bg-slate-200 dark:hover:bg-navy-800 border border-slate-300 dark:border-navy-700 text-slate-700 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white transition-colors"
              title="GitHub Organization (github.com/workouse)"
              aria-label="GitHub Repository"
            >
              <Github className="w-4 h-4" />
            </a>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Theme Toggle (Light / Dark) */}
            <ThemeToggle />

            {/* Primary Action Button */}
            <Button size="sm" onClick={scrollToWaitlist} className="gap-1.5 font-semibold shadow-gold-glow">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{t.nav.joinWaitlist}</span>
            </Button>
          </div>

          {/* Mobile Menu Trigger */}
          <div className="flex items-center gap-2 sm:hidden">
            <ThemeToggle />
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-slate-100 dark:bg-navy-850 border border-slate-300 dark:border-navy-700 text-slate-800 dark:text-slate-300"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-slate-200 dark:border-navy-800 bg-white/95 dark:bg-navy-950/95 backdrop-blur-xl rounded-2xl p-5 space-y-3 shadow-2xl border border-slate-200 dark:border-navy-700 animate-fade-in">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-navy-850">
              <a
                href="https://github.com/workouse"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('github', 'https://github.com/workouse')}
                className="flex items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-300"
              >
                <Github className="w-4 h-4" />
                <span>github.com/workouse</span>
              </a>
              <span className="text-[10px] font-mono text-gold-600 dark:text-gold-400 font-bold bg-gold-500/10 px-2 py-0.5 rounded">
                Open Source
              </span>
            </div>

            <a
              href="#features"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#features', t.nav.features);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.features}
            </a>
            <a
              href="#dlp"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#dlp', t.nav.dlpVault);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.dlpVault}
            </a>
            <a
              href="#budget"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#budget', t.nav.budget);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.budget}
            </a>
            <a
              href="#analytics"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#analytics', t.nav.analytics);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.analytics}
            </a>
            <a
              href="#architecture"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#architecture', t.nav.architecture);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.architecture}
            </a>
            <a
              href="#faq"
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavClick('#faq', t.nav.faq);
              }}
              className="block py-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400"
            >
              {t.nav.faq}
            </a>

            <div className="pt-2">
              <Button size="md" onClick={scrollToWaitlist} className="w-full shadow-gold-glow">
                {t.nav.joinWaitlist}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
